import { useEffect, useState } from "react";
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, Paper, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { apiRequest } from "../utils/http";
import { getAdminUser } from "../utils/auth";
import { createInventoryPdf, INVENTORY_LOGO } from "../utils/inventoryPdf";
import { inventoryProduct } from "../utils/inventoryProducts";
import "./inventory.css";

function Photo({ src, alt }) {
  const [failed, setFailed] = useState(false);
  return src && !failed ? <img src={src} alt={alt} onError={() => setFailed(true)} /> : <span>Image unavailable</span>;
}
export function InventoryCard({ item }) {
  const images = item.images || [];
  return <article className="inventory-card">
    <div className="inventory-main"><Photo src={images[0]} alt={item.title} /></div>
    <div className="inventory-bottom"><div className="inventory-left">
      <Photo src={images[1] || images[0]} alt={`${item.title} alternate view`} />
      <div className="inventory-info"><strong>{item.title}</strong><span>Quantity: {item.quantity}</span><span>Price: &#8358;{Number(item.price).toLocaleString("en-NG")}</span></div>
    </div><div className="inventory-right"><Photo src={images[2] || images[1] || images[0]} alt={`${item.title} detail view`} /></div></div>
  </article>;
}
async function currentProducts() {
  const response = await apiRequest("/api/products", { cache: "no-store" });
  return (response.data || []).filter(product => product.isPublished !== false).map(inventoryProduct);
}
export default function InventoryPage() {
  const role = getAdminUser()?.role;
  const [items, setItems] = useState([]), [legacy, setLegacy] = useState(null);
  const [preview, setPreview] = useState(null), [error, setError] = useState("");
  const [pdfPreview, setPdfPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  async function load() {
    setBusy(true); setError("");
    try { setItems(await currentProducts()); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => () => { if (pdfPreview) URL.revokeObjectURL(pdfPreview.url); }, [pdfPreview]);
  async function download(showPreview = false) {
    setBusy(true); setError("");
    try {
      const products = await currentProducts();
      setItems(products);
      const settings = await apiRequest("/api/site-settings").catch(() => null);
      const pdf = await createInventoryPdf(products, { logoUrl: settings?.data?.logoUrl || INVENTORY_LOGO });
      if (showPreview) setPdfPreview({ url: URL.createObjectURL(pdf.output("blob")) });
      else pdf.save("Gentle-Events-Inventory.pdf");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function archive() {
    setBusy(true); setError("");
    try {
      const rows = [];
      for (let page = 1; ; page++) {
        const response = await apiRequest(`/api/admin/inventory?page=${page}&limit=100`);
        rows.push(...response.data);
        if (!response.hasMore) break;
      }
      setLegacy(rows);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  return <Stack spacing={3}>
    <Typography variant="h4">Inventory catalogue</Typography>
    <Alert severity="info">This catalogue and its PDF use main Products. Create or edit a product once in Products; no separate Inventory upload is needed. Legacy records are preserved, not merged or deleted.</Alert>
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      {role === "super_admin" && <Button component={Link} to="/products" variant="contained">Manage main products</Button>}
      {["super_admin", "business_manager"].includes(role) && <>
        <Button disabled={busy} variant="outlined" onClick={() => download()}>Download Inventory PDF</Button>
        <Button disabled={busy} variant="outlined" onClick={() => download(true)}>Preview catalogue PDF</Button>
      </>}
      <Button disabled={busy} onClick={load}>Refresh</Button>
      <Button disabled={busy} onClick={archive}>View preserved legacy records</Button>
    </Stack>
    {error && <Alert severity="error">{error}</Alert>}
    {busy && <Typography role="status">Preparing catalogue...</Typography>}
    {!busy && !items.length && <Typography>No published products yet.</Typography>}
    {items.map(item => <Paper key={item._id} sx={{ p: 2 }}><Stack spacing={1}>
      <Typography variant="h6">{item.title}</Typography>
      <Typography>{item.quantity} available units · &#8358;{Number(item.price).toLocaleString("en-NG")}</Typography>
      <Button onClick={() => setPreview(item)}>Preview product</Button>
    </Stack></Paper>)}
    <Dialog open={Boolean(pdfPreview)} onClose={() => setPdfPreview(null)} fullWidth maxWidth="lg"><DialogTitle>Inventory catalogue PDF</DialogTitle><DialogContent>
      {pdfPreview && <>
        <Button component="a" href={pdfPreview.url} download="Gentle-Events-Inventory.pdf">Download this PDF</Button>
        <Button component="a" href={pdfPreview.url} target="_blank" rel="noopener noreferrer">Open PDF in new tab</Button>
        <Box component="iframe" title="Inventory catalogue PDF preview" src={pdfPreview.url} sx={{ width: "100%", height: "70vh", border: 0 }} />
      </>}
      <Button onClick={() => setPdfPreview(null)}>Close</Button>
    </DialogContent></Dialog>
    <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="sm"><DialogTitle>Inventory preview</DialogTitle><DialogContent>{preview && <InventoryCard key={preview._id} item={preview} />}<Button onClick={() => setPreview(null)}>Close</Button></DialogContent></Dialog>
    <Dialog open={legacy !== null} onClose={() => setLegacy(null)} fullWidth maxWidth="md"><DialogTitle>Preserved legacy Inventory records</DialogTitle><DialogContent>
      <Alert severity="warning">These {legacy?.length || 0} records are read-only here and no longer drive the new catalogue or PDF. Review any unique images or descriptions before planning a migration.</Alert>
      {legacy?.map(item => <Box key={item._id} sx={{ my: 3 }}><Typography variant="h6">{item.title}</Typography><Typography>{item.quantity} units · &#8358;{Number(item.price).toLocaleString("en-NG")} · {item.status}</Typography><Typography>Linked product: {item.linkedProductTitle}</Typography><Typography>{item.description}</Typography><Stack direction="row" spacing={1}>{item.images.map((src, i) => <Box key={i} component="img" src={src} alt={`${item.title} legacy view ${i + 1}`} sx={{ width: "30%", height: 100, objectFit: "contain" }} />)}</Stack></Box>)}
      <Button onClick={() => setLegacy(null)}>Close</Button>
    </DialogContent></Dialog>
  </Stack>;
}
